import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Share2, Link2, Check, Image, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { toBlob } from 'html-to-image';
import axios from 'axios';
import { BACKEND_URL as API } from '@/utils/apiConfig';
import { shareViaNative } from '@/utils/nativeShare';
import ShareToFriendModal from '@/components/ShareToFriendModal';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
import { SHARE_THEMES } from '@/components/shareThemes';

import { isDespiaIOS, saveToIOSPhotos } from '@/utils/shareCard';
const THEMES = SHARE_THEMES;

function stripMarkdown(text) {
  let clean = text;
  clean = clean.replace(/!\[.*?\]\(.*?\)/g, '');
  clean = clean.replace(/^#{1,6}\s+/gm, '');
  clean = clean.replace(/\*\*\*(.*?)\*\*\*/g, '$1');
  clean = clean.replace(/\*\*(.*?)\*\*/g, '$1');
  clean = clean.replace(/\*(.*?)\*/g, '$1');
  clean = clean.replace(/\[(.*?)\]\(.*?\)/g, '$1');
  clean = clean.replace(/^[-*+]\s+/gm, '\u2022 ');
  clean = clean.replace(/\n{3,}/g, '\n\n');
  return clean.trim();
}

function stripFollowUpQuestion(text) {
  const paragraphs = text.split('\n\n');
  const patterns = [
    /^would you like/i, /^do you want/i, /^shall (i|we)/i, /^should (i|we)/i,
    /^is there (anything|something|a specific)/i, /^are there (any|other)/i,
    /^did (that|this) (make sense|help|answer)/i, /^does (that|this) (make sense|help|answer)/i,
    /^what (would|do|else|other|aspect)/i, /^how (would|do|can|else)/i,
    /^let me know/i, /^feel free/i, /^i'd love to/i, /^i can (also|help|explore|provide)/i,
  ];
  const filtered = paragraphs.filter((p) => {
    const t = p.trim();
    if (t.length >= 200) return true;
    if (t.endsWith('?') || patterns.some((re) => re.test(t))) return false;
    return true;
  });
  return filtered.join('\n\n').trimEnd();
}

function truncateText(text, maxLen = 600) {
  if (text.length <= maxLen) return text;
  const truncated = text.substring(0, maxLen);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastNewline = truncated.lastIndexOf('\n');
  const cutAt = Math.max(lastPeriod, lastNewline, maxLen - 50);
  return truncated.substring(0, cutAt + 1) + '\n\n...';
}

export default function ShareModal({ message, conversationTitle, onClose }) {
  const { t } = useTranslation();
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [generating, setGenerating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [summary, setSummary] = useState(message.share_summary || null);
  // Friend share — opens the in-app per-friend thread composer (parity
  // with ShareReflectionModal). Mounted lazily so the chat ShareModal
  // surface stays the same five themed swatches + four action buttons.
  const [showShareToFriend, setShowShareToFriend] = useState(false);
  const { enabled: communityShareEnabled } = useFeatureFlag('community_share');
  const cardRef = useRef(null);
  const generatingRef = useRef(false);

  const theme = THEMES.find((t) => t.key === selectedTheme) || THEMES[0];

  // Render the card with the polished `summary` if we have it, otherwise
  // fall back to the cleaned/truncated raw `message.content` so the card
  // renders IMMEDIATELY on mount with valid content. This is the key
  // alignment with ShareReflectionModal — reflection has all data on
  // mount, so its card renders instantly and pre-bake completes in
  // ~200ms. Previously chat hid the card behind a loading spinner while
  // the API resolved, which left cardRef.current=null and blocked the
  // pre-bake; on Android the user clicked Share before pre-bake could
  // catch up, causing the fallback download path to run.
  const fallbackText = React.useMemo(() => {
    const raw = message.content || '';
    return truncateText(stripFollowUpQuestion(stripMarkdown(raw)));
  }, [message.content]);
  const displayText = summary || fallbackText;

  // Pre-baked PNG File for sharing. Computed proactively in the
  // background as soon as the preview card is rendered so that on
  // click the user-activation token is still fresh and `navigator.share`
  // fires synchronously — critical for Android, where html2canvas
  // taking 200-500ms on the click would expire the activation and
  // cause the share sheet to silently not appear.
  // Mirrors the exact pattern used in ShareReflectionModal.js which is
  // confirmed working on Android Despia.
  const [preparedFile, setPreparedFile] = useState(null);
  const preparedKeyRef = useRef(null);  // tracks which (theme,text) the file was made for

  // Create share record with the current theme, return shareUrl.
  // Does NOT touch any rendering-state (no setLoadingSummary). The
  // card is already rendered with fallback text — the API summary is
  // a polish step that triggers a re-bake when it arrives.
  const ensureShareUrl = useCallback(async (themeKey) => {
    if (shareUrl) return shareUrl;
    try {
      const response = await axios.post(`${API}/api/share`, {
        message_id: message.message_id,
        theme: themeKey,
      }, { withCredentials: true });
      const url = `${window.location.origin}/api/og/share/${response.data.share_id}`;
      setShareUrl(url);
      if (response.data.summary) setSummary(response.data.summary);
      return url;
    } catch {
      return null;
    }
  }, [shareUrl, message.message_id]);

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return null;
    const blob = await toBlob(cardRef.current, {
      pixelRatio: 2,
      skipAutoScale: true,
      cacheBust: false,
    });
    return blob;
  }, []);

  // Eagerly resolve the share URL on mount.
  React.useEffect(() => {
    ensureShareUrl(selectedTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-bake the PNG in the background — mirrors ReflectionModal exactly.
  // Key is ONLY the theme. Bakes once on mount, once per theme switch.
  // When the summary arrives and the card re-renders, we do a silent
  // re-bake but the button stays enabled with the previous image.
  const bakeGenRef = useRef(0);
  React.useEffect(() => {
    if (!cardRef.current || !displayText) return;
    bakeGenRef.current++;
    const gen = bakeGenRef.current;
    (async () => {
      try {
        const blob = await generateImage();
        if (bakeGenRef.current !== gen || !blob) return;
        const file = new File([blob], 'eili-insight.png', { type: 'image/png' });
        preparedKeyRef.current = selectedTheme;
        setPreparedFile(file);
      } catch (err) {
        console.warn('[ShareModal] pre-bake failed:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTheme]);

  // Upload the pre-baked PNG as OG image — runs once when first ready,
  // and again if the file changes (theme switch). Background, non-blocking.
  const ogUploadedRef = useRef(null);
  React.useEffect(() => {
    if (!preparedFile || !shareUrl) return;
    const match = shareUrl.match(/\/share\/([^/]+)$/);
    if (!match) return;
    const shareId = match[1];
    const key = `${shareId}|${preparedFile.size}`;
    if (ogUploadedRef.current === key) return;
    ogUploadedRef.current = key;
    const form = new FormData();
    form.append('file', preparedFile, 'eili-share.png');
    fetch(`${API}/api/share/${shareId}/og-image`, { method: 'POST', body: form, credentials: 'include' }).catch(() => {});
  }, [preparedFile, shareUrl]);


  const handleDownload = async () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    try {
      const blob = await generateImage();
      if (!blob) return;

      // Android Despia: <a download> doesn't work in WebView.
      // Upload and use despia() file bridge which saves to device.
      const isDespiaAndroid = document.body?.classList?.contains('despia-android');
      if (isDespiaAndroid) {
        toast.info(t('generated.sharemodal_saving_to_device_72fe'));
        const form = new FormData();
        form.append('file', new File([blob], 'eili-insight.png', { type: 'image/png' }), 'eili-insight.png');
        const res = await fetch(`${API}/api/share/upload-image`, { method: 'POST', body: form, credentials: 'include' });
        const data = await res.json();
        if (data?.url) {
          const { default: despia } = await import('despia-native');
          despia(data.url);
        }
      } else if (isDespiaIOS()) {
        await saveToIOSPhotos(blob, 'eili-insight.png');
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = 'eili-insight.png';
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(t('share.image_saved'));
      }
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  const handleShareImage = async () => {
    if (generatingRef.current) return;
    const file = preparedFile;
    if (!file) {
      toast.error(t('share.share_failed'));
      return;
    }

    // Fetch share URL on demand if not ready yet (non-blocking for iOS
    // where the file is what matters; needed for Android's shareapp://)
    let url = shareUrl;
    if (!url) url = await ensureShareUrl(selectedTheme);

    const result = await shareViaNative({
      title: t('generated.sharemodal_eili_biblical_insight_14ed'),
      text: t('generated.sharemodal_biblical_insight_055a'),
      url: url || undefined,
      file,
    });

    if (!result.shared && !result.aborted) {
      console.warn('[ShareModal] share failed:', result);
      toast.error(t('share.share_failed'));
    }
  };

  const handleCopyLink = async () => {
    const url = await ensureShareUrl(selectedTheme);
    if (!url) {
      toast.error(t('share.link_error'));
      return;
    }
    await navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success(t('share.link_copied'));
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="share-modal"
    >
      <div
        className="relative w-full max-w-lg flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, #334155)',
          maxHeight: 'min(90vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <div className="flex items-center gap-2">
            <Image className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.sharemodal_share_as_image_c705')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition hover:bg-white/10"
            data-testid="share-modal-close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Theme Picker */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharemodal_style_e576')}
            </p>
            <div className="flex gap-2.5 flex-wrap" data-testid="share-theme-picker">
              {THEMES.map((t) => {
                const active = selectedTheme === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => { setSelectedTheme(t.key); }}
                    className="flex flex-col items-center gap-1.5 transition-all"
                    data-testid={`share-theme-${t.key}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full relative overflow-hidden transition-all"
                      style={{
                        background: t.swatch[0],
                        border: active ? `2.5px solid ${t.swatch[1]}` : '2px solid transparent',
                        boxShadow: active ? `0 0 0 2px ${t.swatch[1]}40` : 'none',
                      }}
                    >
                      <div
                        className="absolute bottom-0 right-0 w-5 h-5 rounded-tl-full"
                        style={{ background: t.swatch[1] }}
                      />
                    </div>
                    <span
                      className="text-[10px] font-medium"
                      style={{ color: active ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}
                    >
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Image Preview */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharemodal_preview_5e55')}
            </p>
            <div className="rounded-xl overflow-hidden relative" style={{ border: '1px solid var(--glass-border)' }}>
              {/* Card always rendered so cardRef.current is set immediately
                  and the pre-bake useEffect can fire on mount — exactly
                  like ShareReflectionModal does. While the polished
                  summary loads, the card displays the cleaned raw
                  message content so the preview is never blank. */}
              <div ref={cardRef} data-testid="share-image-preview">
                <ShareCard theme={theme} text={displayText} />
              </div>
              {!summary && (
                <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm" data-testid="share-summary-polishing">
                  <div className="h-2.5 w-2.5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: theme.accent, borderTopColor: 'transparent' }} />
                  <span className="text-[10px] font-medium text-white/90">{t('generated.sharemodal_polishing_b448')}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t flex flex-wrap gap-2.5" style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <Button
            onClick={handleShareImage}
            disabled={generating || !preparedFile}
            className="flex-1 min-w-[120px] rounded-xl py-4 font-medium gap-2"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid="share-image-btn"
          >
            {!preparedFile && !generating ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('generated.sharemodal_preparing_ca85')}
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                {generating ? t('share.generating') : (document.body?.classList?.contains('despia-android') ? 'Share' : t('share.share_image'))}
              </>
            )}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={generating}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-download-btn"
          >
            <Download className="h-4 w-4" />
            {t('generated.sharemodal_save_056c')}
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-copy-link-btn"
          >
            {linkCopied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {linkCopied ? t('share.copied') : t('share.link')}
          </Button>
          {communityShareEnabled && (
          <Button
            onClick={() => setShowShareToFriend(true)}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-to-friend-btn"
          >
            <Users className="h-4 w-4" />
            {t('generated.sharemodal_friend_a850')}
          </Button>
          )}
        </div>
      </div>
      {/* In-app share-to-friend composer (parity with reflection share) */}
      {showShareToFriend && (
        <ShareToFriendModal
          message={message}
          conversationTitle={conversationTitle}
          onClose={() => setShowShareToFriend(false)}
        />
      )}
    </div>
  );
}

/* The actual card that gets rendered to an image */
function ShareCard({ theme, text }) {
  const {
    t
  } = useTranslation();

  return (
    <div
      style={{
        background: theme.bg,
        padding: '32px 28px 24px',
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        minHeight: 200,
        position: 'relative',
      }}
    >
      {/* Accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 4,
          background: theme.accent,
        }}
      />
      {/* Branding — standard HTML elements only (no SVG — html-to-image
          uses SVG foreignObject internally, nested SVG causes hangs) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <img
          src="/logo.png"
          alt={t('generated.sharemodal_eili_01c7')}
          style={{ width: 22, height: 22, objectFit: 'contain' }}
          crossOrigin="anonymous"
        />
        <span
          style={{
            fontFamily: "'Playwrite ES Deco', cursive",
            fontSize: 16,
            fontWeight: 700,
            color: theme.accent,
          }}
        >
          {t('generated.sharemodal_eili_01c7')}
        </span>
        <span style={{ fontSize: 11, color: theme.secondary }}>
          {t('generated.sharemodal_the_word_first_a905')}
        </span>
      </div>
      {/* Divider */}
      <div style={{ height: 1, background: theme.border, marginBottom: 18 }} />
      {/* Text */}
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.7,
          color: theme.text,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {text}
      </div>
      {/* Footer */}
      <div
        style={{
          marginTop: 20,
          paddingTop: 14,
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 11, color: theme.secondary }}>
          {t('generated.sharemodal_tap_the_link_d58a')}
        </span>
      </div>
    </div>
  );
}
